import React, { useMemo } from "react";
import { Avatar } from "antd";
import { AvatarConfig } from "../types/user";
import {
  EquippedRewardState,
  getEquippedBadgeReward,
  getEquippedFrameReward,
  getEquippedHatAccessory,
  getRewardBadgeVisualValue,
  getRewardOverlayVisualValue,
  isImageSource,
  normalizeRewardCollection,
} from "../utils/rewards";

interface RewardedProfileDisplayProps {
  src?: string;
  alt: string;
  fallbackSrc: string;
  width: number | string;
  height?: number | string;
  variant?: "circle" | "portrait";
  border?: string;
  boxShadow?: string;
  backgroundColor?: string;
  rewards?: unknown;
  avatarConfig?: AvatarConfig;
  equippedState?: EquippedRewardState;
  overlayMode?: "compact" | "full";
  overlayImageSx?: React.CSSProperties;
  badgeSize?: number;
  overlaySize?: number;
}

const RewardedProfileDisplay: React.FC<RewardedProfileDisplayProps> = ({
  src,
  alt,
  fallbackSrc,
  width,
  height,
  variant = "circle",
  border,
  boxShadow,
  backgroundColor,
  rewards,
  avatarConfig,
  equippedState,
  overlayMode = "compact",
  overlayImageSx,
  badgeSize,
  overlaySize,
}) => {
  const normalizedRewards = useMemo(
    () => normalizeRewardCollection(rewards),
    [rewards],
  );
  const equippedHat = avatarConfig
    ? getEquippedHatAccessory(avatarConfig)
    : null;
  const equippedFrameReward = getEquippedFrameReward(
    normalizedRewards,
    equippedState,
  );
  const equippedBadgeReward = getEquippedBadgeReward(
    normalizedRewards,
    equippedState,
  );
  const overlayValue =
    equippedHat?.image || getRewardOverlayVisualValue(equippedFrameReward);
  const badgeValue = getRewardBadgeVisualValue(equippedBadgeReward);
  const resolvedImage = src && src.trim() ? src : fallbackSrc;
  const resolvedBadgeSize = badgeSize ?? (variant === "circle" ? 22 : 28);
  const resolvedOverlaySize = overlaySize ?? (variant === "circle" ? 28 : 40);
  const shouldUseFullOverlay = Boolean(
    overlayValue && overlayMode === "full" && isImageSource(overlayValue),
  );

  const renderVisual = (value: string, size: number) => {
    if (isImageSource(value)) {
      return (
        <img
          src={value}
          alt="Recompensa equipada"
          style={{ width: size, height: size, objectFit: "contain" }}
        />
      );
    }

    return (
      <span
        style={{
          fontSize: `${Math.max(16, size * 0.82)}px`,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {value}
      </span>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {variant === "circle" ? (
        <Avatar
          src={resolvedImage}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            border,
            boxShadow,
            backgroundColor,
          }}
        />
      ) : (
        <img
          src={resolvedImage}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            border,
            boxShadow,
            borderRadius: 10,
            backgroundColor,
          }}
        />
      )}

      {shouldUseFullOverlay ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <img
            src={overlayValue}
            alt="Recompensa equipada"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              position: "relative",
              ...overlayImageSx,
            }}
          />
        </div>
      ) : overlayValue ? (
        <div
          style={{
            position: "absolute",
            top: variant === "circle" ? -6 : 6,
            left: "50%",
            transform: "translateX(-50%)",
            minWidth: resolvedOverlaySize,
            minHeight: resolvedOverlaySize,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {renderVisual(overlayValue, resolvedOverlaySize)}
        </div>
      ) : null}

      {badgeValue ? (
        <div
          style={{
            position: "absolute",
            right: variant === "circle" ? -2 : 8,
            bottom: variant === "circle" ? -2 : 8,
            width: resolvedBadgeSize,
            height: resolvedBadgeSize,
            borderRadius: "50%",
            background: "var(--dashboard-yellow)",
            border: "2px solid var(--dashboard-header-blue)",
            boxShadow: "0 2px 0 rgba(47, 43, 116, 0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            overflow: "hidden",
          }}
        >
          {renderVisual(badgeValue, resolvedBadgeSize - 4)}
        </div>
      ) : null}
    </div>
  );
};

export default RewardedProfileDisplay;
